import type {
  CheckboxProps,
  CheckboxRef,
  CheckboxValue,
  CommonComponentProps,
  FieldProps,
  ImagesHolderProps,
  ImagesHolderValue,
  InputProps,
  InputRef,
  InputValue,
  MoneyInputProps,
  MoneyInputRef,
  MoneyInputValue,
  NumberInputProps,
  NumberInputRef,
  NumberInputValue,
  SelectProps,
  SelectRef,
  SelectValue,
  TaxSelectorProps,
  TaxSelectorRef,
  TaxSelectorValue,
  TextEditorProps,
  TextEditorRef,
  TextEditorValue,
  TitleProps,
  TitleRef,
  TitleValue,
} from "@hubrise/react-components"
import { Field } from "@hubrise/react-components"
import * as React from "react"

import useStatefulField from "shared/hooks/useStatefulField"

type StatefulFieldProps<Props, Value> = {
  initialValue: Value
  onChange: (value: Value) => void
  debounce?: boolean
  editorError?: string | undefined
  validate?: (newValue: Value) => string | undefined
} & Omit<FieldProps & Props, "value" | "error" | "onChange" | "onBlur">

const useJsxElement = <Props extends CommonComponentProps<Value>, Value, TRef>(
  FieldComponent: React.ComponentType<FieldProps & Props>,
  { initialValue, onChange, editorError, validate, debounce = true, ...otherProps }: StatefulFieldProps<Props, Value>,
  $ref?: React.ForwardedRef<TRef>,
): JSX.Element => {
  const { value, error, handleChange, handleBlur } = useStatefulField<Value>({
    initialValue,
    onChange,
    editorError,
    validate,
    debounce,
  })

  const componentProps = {
    ...otherProps,
    value,
    error,
    onChange: handleChange,
    onBlur: handleBlur,
  } as FieldProps & Props

  return $ref ? <FieldComponent ref={$ref} {...componentProps} /> : <FieldComponent {...componentProps} />
}

const generateStatefulField = <Props extends CommonComponentProps<Value>, Value>(
  FieldComponent: React.ComponentType<FieldProps & Props>,
) => {
  const StatefulField = (props: StatefulFieldProps<Props, Value>): JSX.Element =>
    useJsxElement<Props, Value, unknown>(FieldComponent, props)
  StatefulField.displayName = [FieldComponent.displayName, "StatefulField"].join(" - ")
  return StatefulField
}

const generateStatefulFieldWithRef = <Props extends CommonComponentProps<Value>, Value, TRef>(
  FieldComponent: React.ComponentType<FieldProps & Props>,
) => {
  const StatefulField = React.forwardRef(
    (props: StatefulFieldProps<Props, Value>, $ref: React.ForwardedRef<TRef>): JSX.Element =>
      useJsxElement<Props, Value, TRef>(FieldComponent, props, $ref),
  )
  StatefulField.displayName = [FieldComponent.displayName, "StatefulField"].join(" - ")
  return StatefulField
}

const StatefulField = {
  Checkbox: generateStatefulFieldWithRef<CheckboxProps, CheckboxValue, CheckboxRef>(Field.Checkbox),
  ImagesHolder: generateStatefulField<ImagesHolderProps, ImagesHolderValue>(Field.ImagesHolder),
  Input: generateStatefulFieldWithRef<InputProps, InputValue, InputRef>(Field.Input),
  MoneyInput: generateStatefulFieldWithRef<MoneyInputProps, MoneyInputValue, MoneyInputRef>(Field.MoneyInput),
  NumberIput: generateStatefulFieldWithRef<NumberInputProps, NumberInputValue, NumberInputRef>(Field.NumberInput),
  Select: generateStatefulFieldWithRef<SelectProps, SelectValue, SelectRef>(Field.Select),
  TaxSelector: generateStatefulFieldWithRef<TaxSelectorProps, TaxSelectorValue, TaxSelectorRef>(Field.TaxSelector),
  TextEditor: generateStatefulFieldWithRef<TextEditorProps, TextEditorValue, TextEditorRef>(Field.TextEditor),
  Title: generateStatefulFieldWithRef<TitleProps, TitleValue, TitleRef>(Field.Title),
} as const

export default StatefulField
